import React, { useEffect, useState } from "react";
import { FrTaxCard } from "@/components/admin/FrTaxCard";
import { api, formatError, SERVICES } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Save } from "lucide-react";

export default function Settings() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [country, setCountry] = useState("NL");

  useEffect(() => {
    setS(null);
    api.get(`/settings?country=${country}`).then((r) => setS(r.data));
  }, [country]);

  if (!s) return <div className="text-slate-500">Loading…</div>;

  const updSvc = (svc, k, v) => setS({ ...s, services: { ...s.services, [svc]: { ...s.services[svc], [k]: v } } });
  const updFreq = (k, v) => setS({ ...s, frequency_discount: { ...s.frequency_discount, [k]: parseFloat(v) || 0 } });
  const updCancel = (k, v) => setS({ ...s, cancellation: { ...s.cancellation, [k]: parseFloat(v) || 0 } });

  const save = async () => {
    setSaving(true);
    try {
      // sanitize numeric strings
      const out = { ...s };
      out.vat_residential_pct = parseFloat(out.vat_residential_pct) || 0;
      out.vat_commercial_pct = parseFloat(out.vat_commercial_pct) || 0;
      out.multi_level_pct_per_floor = parseFloat(out.multi_level_pct_per_floor) || 0;
      out.services = Object.fromEntries(Object.entries(out.services).map(([k, v]) => [k, {
        ...v,
        hourly_rate: parseFloat(v.hourly_rate) || 0,
        min_hours: parseFloat(v.min_hours) || 0,
        base_sqm: parseFloat(v.base_sqm) || 0,
        extra_min_per_10sqm: parseFloat(v.extra_min_per_10sqm) || 0,
      }]));
      await api.put(`/settings?country=${country}`, out);
      toast.success(`${country === "FR" ? "France" : "Netherlands"} settings saved`);
    } catch (e) { toast.error(formatError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div>
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-[#1B3A6B]">Settings</h1>
          <p className="text-sm text-slate-500 mt-1">Pricing, VAT, and cancellation policy — editing <b data-testid="settings-country-label">{country === "FR" ? "France (French Riviera)" : "Netherlands"}</b>.</p>
          <div className="mt-3 inline-flex rounded-full bg-slate-100 p-0.5 ring-1 ring-slate-200" data-testid="settings-country-toggle">
            {[["NL", "Netherlands"], ["FR", "France"]].map(([c, l]) => (
              <button key={c} type="button" onClick={() => setCountry(c)} data-testid={`settings-country-${c.toLowerCase()}`}
                className={`px-4 h-8 rounded-full text-xs font-semibold transition-colors ${country === c ? "bg-white text-[#1B3A6B] shadow-sm" : "text-slate-500 hover:text-[#1B3A6B]"}`}>{l}</button>
            ))}
          </div>
          {country === "FR" && <FrTaxCard />}
        </div>
        <Button onClick={save} disabled={saving} className="bg-[#1B3A6B] hover:bg-[#152D54] text-white" data-testid="settings-save-btn">
          <Save className="w-4 h-4 mr-1" /> {saving ? "Saving…" : "Save settings"}
        </Button>
      </div>

      {/* Service pricing */}
      <div className="mt-6 bg-white rounded-xl ring-1 ring-slate-200 p-6">
        <h2 className="font-heading font-semibold text-lg text-[#0F172A]">Service pricing</h2>
        <p className="text-xs text-slate-500 mt-1">Hourly rate, minimum hours, and how m² extends the cleaning time.</p>
        <div className="mt-4 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Service</TableHead>
                <TableHead>€/hour</TableHead>
                <TableHead>Min hours</TableHead>
                <TableHead>Base m²</TableHead>
                <TableHead>Extra min / 10 m²</TableHead>
                <TableHead>Category</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {Object.entries(s.services || {}).map(([name, cfg]) => (
                <TableRow key={name}>
                  <TableCell className="font-medium">{name}</TableCell>
                  <TableCell><Input type="number" value={cfg.hourly_rate} onChange={(e) => updSvc(name, "hourly_rate", e.target.value)} className="w-24" data-testid={`settings-rate-${name}`} /></TableCell>
                  <TableCell><Input type="number" step="0.5" value={cfg.min_hours} onChange={(e) => updSvc(name, "min_hours", e.target.value)} className="w-24" /></TableCell>
                  <TableCell><Input type="number" value={cfg.base_sqm} onChange={(e) => updSvc(name, "base_sqm", e.target.value)} className="w-24" /></TableCell>
                  <TableCell><Input type="number" value={cfg.extra_min_per_10sqm} onChange={(e) => updSvc(name, "extra_min_per_10sqm", e.target.value)} className="w-24" /></TableCell>
                  <TableCell><span className="pill bg-slate-100 text-slate-700">{cfg.category}</span></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* VAT + Multi-level */}
      <div className="mt-6 grid md:grid-cols-3 gap-4">
        <Card title="VAT (residential)">
          <Input type="number" step="0.1" value={s.vat_residential_pct} onChange={(e) => setS({ ...s, vat_residential_pct: e.target.value })} disabled={country === "FR"} title={country === "FR" ? "Managed in the protected French VAT regime card above" : undefined} data-testid="settings-vat-res" />
          <p className="text-xs text-slate-500 mt-2">Applied to residential & private bookings.</p>
        </Card>
        <Card title="VAT (commercial / B2B)">
          <Input type="number" step="0.1" value={s.vat_commercial_pct} onChange={(e) => setS({ ...s, vat_commercial_pct: e.target.value })} disabled={country === "FR"} title={country === "FR" ? "Managed in the protected French VAT regime card above" : undefined} data-testid="settings-vat-com" />
          <p className="text-xs text-slate-500 mt-2">Applied to office cleaning and B2B-flagged bookings.</p>
        </Card>
        <Card title="Multi-level surcharge">
          <Input type="number" step="0.1" value={s.multi_level_pct_per_floor} onChange={(e) => setS({ ...s, multi_level_pct_per_floor: e.target.value })} data-testid="settings-multilevel" />
          <p className="text-xs text-slate-500 mt-2">% added per extra floor above ground (e.g. 3 = +3% per floor).</p>
        </Card>
      </div>

      {/* Frequency discount */}
      <div className="mt-6 bg-white rounded-xl ring-1 ring-slate-200 p-6">
        <h2 className="font-heading font-semibold text-lg text-[#0F172A]">Frequency discounts (%)</h2>
        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
          {Object.entries(s.frequency_discount || {}).map(([k, v]) => (
            <div key={k}>
              <Label className="capitalize">{k}</Label>
              <Input type="number" step="0.5" value={v} onChange={(e) => updFreq(k, e.target.value)} className="mt-1.5" data-testid={`settings-freq-${k}`} />
            </div>
          ))}
        </div>
      </div>

      {/* Cancellation */}
      <div className="mt-6 bg-white rounded-xl ring-1 ring-slate-200 p-6">
        <h2 className="font-heading font-semibold text-lg text-[#0F172A]">Cancellation & Reschedule</h2>
        <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <div>
            <Label>Free advance window (hours)</Label>
            <Input type="number" value={s.cancellation?.free_advance_hours || 24} onChange={(e) => updCancel("free_advance_hours", e.target.value)} className="mt-1.5" data-testid="settings-free-hours" />
          </div>
          <div>
            <Label>Same-day cancel %</Label>
            <Input type="number" value={s.cancellation?.same_day_cancellation_pct || 50} onChange={(e) => updCancel("same_day_cancellation_pct", e.target.value)} className="mt-1.5" />
          </div>
          <div>
            <Label>No-show %</Label>
            <Input type="number" value={s.cancellation?.no_show_pct || 100} onChange={(e) => updCancel("no_show_pct", e.target.value)} className="mt-1.5" />
          </div>
          <div>
            <Label>No-show reschedule fee (€)</Label>
            <Input type="number" value={s.cancellation?.no_show_reschedule_fee || 75} onChange={(e) => updCancel("no_show_reschedule_fee", e.target.value)} className="mt-1.5" />
          </div>
          <div>
            <Label>Same-day reschedule fee (€)</Label>
            <Input type="number" value={s.cancellation?.same_day_reschedule_fee || 50} onChange={(e) => updCancel("same_day_reschedule_fee", e.target.value)} className="mt-1.5" />
          </div>
        </div>
      </div>
    </div>
  );
}

function Card({ title, children }) {
  return (
    <div className="bg-white rounded-xl ring-1 ring-slate-200 p-5">
      <Label className="text-xs uppercase tracking-wide text-slate-500 font-semibold">{title}</Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}
